import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { globalChatsService, adminActionItemsService } from '../../services/db';
import { ADMIN_ACTION_TYPES } from '../../constants';
import { CheckCircleIcon, SupportIcon, CloseIcon, SendIcon } from '../common/Icons';

export const UserSupportChatWidget = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMsg, setInputMsg] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);

  // Guest inquiry state
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestMessage, setGuestMessage] = useState('');
  const [guestSent, setGuestSent] = useState(false);
  const [isSendingGuest, setIsSendingGuest] = useState(false);

  // Subscribe to messages when authenticated
  useEffect(() => {
    if (!user || !user.uid) {
      setMessages([]);
      return;
    }

    const currentUserId = user.uid;
    const unsubscribe = globalChatsService.subscribeMessages(currentUserId, (msgs) => {
      setMessages(msgs);
      // Count unread messages from admin (messages where senderId !== currentUserId and seen === false)
      if (!isOpen) {
        const unread = msgs.filter((m) => m.senderId !== currentUserId && !m.seen).length;
        setUnreadCount(unread);
      }
    });

    return () => unsubscribe();
  }, [user, isOpen]);

  // Mark messages as read when opening chat
  useEffect(() => {
    if (isOpen && user?.uid && messages.length > 0) {
      setUnreadCount(0);
      globalChatsService.markAllAsRead(user.uid, user.uid).catch((e) => console.warn(e));
    }
  }, [isOpen, user, messages.length]);

  // Scroll to bottom
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMsg.trim() || isSending || !user) return;

    const text = inputMsg.trim();
    setInputMsg('');
    setIsSending(true);

    try {
      const senderName = user.displayName || user.email?.split('@')[0] || 'Contestant';
      await globalChatsService.sendMessage(
        user.uid,
        user.uid,
        senderName,
        text,
        '',
        {
          name: senderName,
          email: user.email,
          photoURL: user.photoURL || ''
        }
      );
    } catch (err) {
      console.error('Failed to send support message:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleSendGuestInquiry = async (e) => {
    e.preventDefault();
    if (!guestName.trim() || !guestEmail.trim() || !guestMessage.trim()) return;

    setIsSendingGuest(true);
    try {
      await adminActionItemsService.create({
        type: ADMIN_ACTION_TYPES.globalChat,
        title: `Community Inquiry: ${guestName}`,
        message: guestMessage,
        senderId: guestEmail,
        senderName: guestName,
        data: {
          name: guestName,
          email: guestEmail,
          message: guestMessage,
          isGuestInquiry: true,
        }
      });
      setGuestSent(true);
      setGuestMessage('');
    } catch (err) {
      console.error('Failed to submit guest message:', err);
      alert('Failed to send message. Please try again or email us directly.');
    } finally {
      setIsSendingGuest(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <aside className="support-widget-container" aria-label="Live Support Chat">
      {/* Chat Window Popup */}
      {isOpen && (
        <section className="support-widget-window" aria-label="Support Conversation Window">
          {/* Header */}
          <header className="support-widget-header">
            <div className="support-widget-header__title">
              <span className="support-widget-status-dot" />
              <div>
                <h4>Bible Contest Care Team</h4>
                <small>Dedicated Ministry • We reply as soon as possible</small>
              </div>
            </div>
            <button
              type="button"
              className="support-widget-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close Chat"
            >
              <CloseIcon size={16} />
            </button>
          </header>

          {/* Body */}
          <div className="support-widget-body">
            {!user ? (
              // Unauthenticated state: Guest message form + login option
              <div style={{ padding: '16px' }}>
                {guestSent ? (
                  <div style={{ textAlign: 'center', padding: '24px 8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(5, 150, 105, 0.1)', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckCircleIcon size={28} style={{ color: '#059669' }} />
                      </div>
                    </div>
                    <h4 style={{ color: 'var(--clr-text-1)', marginBottom: '8px' }}>Message Received!</h4>
                    <p style={{ fontSize: '13px', color: 'var(--clr-text-2)', lineHeight: 1.5 }}>
                      Thank you, <strong>{guestName}</strong>! Our ministry team has received your message and will reply to <strong>{guestEmail}</strong> as soon as possible.
                    </p>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => setGuestSent(false)}
                      style={{ marginTop: '16px' }}
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSendGuestInquiry} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', color: 'var(--clr-text-1)' }}>
                        Leave Us a Message
                      </h4>
                      <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--clr-text-2)', lineHeight: 1.4 }}>
                        Have a question about leagues, quizzes, or a prayer request? Send us a note below!
                      </p>
                    </div>

                    <input
                      type="text"
                      placeholder="Your Name *"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      required
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--clr-border)',
                        background: 'var(--clr-bg)',
                        color: 'var(--clr-text-1)',
                        fontSize: '13px'
                      }}
                    />

                    <input
                      type="email"
                      placeholder="Your Email Address *"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      required
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--clr-border)',
                        background: 'var(--clr-bg)',
                        color: 'var(--clr-text-1)',
                        fontSize: '13px'
                      }}
                    />

                    <textarea
                      placeholder="How can we help or pray for you? *"
                      value={guestMessage}
                      onChange={(e) => setGuestMessage(e.target.value)}
                      required
                      rows={3}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--clr-border)',
                        background: 'var(--clr-bg)',
                        color: 'var(--clr-text-1)',
                        fontSize: '13px',
                        resize: 'none'
                      }}
                    />

                    <button
                      type="submit"
                      disabled={isSendingGuest}
                      className="btn btn--primary btn--sm"
                      style={{ width: '100%', marginTop: '4px' }}
                    >
                      {isSendingGuest ? 'Sending...' : '✉️ Send Message to Ministry Team'}
                    </button>

                    <div style={{ textAlign: 'center', marginTop: '6px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--clr-muted)' }}>
                        Already have an account?{' '}
                        <button
                          type="button"
                          onClick={() => { setIsOpen(false); navigate('/login'); }}
                          style={{ background: 'none', border: 'none', color: 'var(--clr-primary)', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontSize: '12px' }}
                        >
                          Sign In for Live Chat
                        </button>
                      </span>
                    </div>
                  </form>
                )}
              </div>
            ) : (
              // Authenticated Chat Flow
              <>
                <div className="support-widget-messages">
                  {/* Ministry Care Notice */}
                  <div
                    style={{
                      background: 'rgba(99, 102, 241, 0.08)',
                      border: '1px solid rgba(99, 102, 241, 0.25)',
                      borderRadius: '10px',
                      padding: '10px 12px',
                      fontSize: '12px',
                      color: 'var(--clr-text-1)',
                      lineHeight: '1.45',
                      marginBottom: '8px',
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'flex-start'
                    }}
                  >
                    <span style={{ fontSize: '15px', flexShrink: 0, marginTop: '1px' }}>🤝</span>
                    <div>
                      <strong>We're Honored to Serve You:</strong> Leave your question, prayer request, or league inquiry below. Our ministry team personally reads every message and will get back to you promptly!
                    </div>
                  </div>

                  <div className="support-widget-welcome-card">
                    <p>
                      👋 <strong>Grace and peace, {user.displayName || 'Friend'}!</strong> How can we assist, encourage, or support your Bible contest journey today?
                    </p>
                  </div>

                  {messages.map((msg) => {
                    const isMe = msg.senderId === user.uid;
                    return (
                      <div
                        key={msg.id}
                        className={`support-message ${isMe ? 'support-message--me' : 'support-message--admin'}`}
                      >
                        <div className="support-message__sender">
                          {isMe ? 'You' : msg.senderName || 'Admin Support'}
                        </div>
                        <div className="support-message__bubble">
                          {msg.text}
                          {msg.imageUrl && (
                            <img
                              src={msg.imageUrl}
                              alt="Attachment"
                              className="support-message__img"
                            />
                          )}
                        </div>
                        <span className="support-message__time">
                          {formatTime(msg.timestamp)}
                        </span>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Footer */}
                <form className="support-widget-input-form" onSubmit={handleSendMessage}>
                  <input
                    type="text"
                    value={inputMsg}
                    onChange={(e) => setInputMsg(e.target.value)}
                    placeholder="Type your question or issue..."
                    disabled={isSending}
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={!inputMsg.trim() || isSending}
                    className="support-widget-send-btn"
                    aria-label="Send message"
                  >
                    {isSending ? '...' : <SendIcon size={15} />}
                  </button>
                </form>
              </>
            )}
          </div>
        </section>
      )}

      {/* Floating Launcher Button */}
      <button
        type="button"
        className="support-widget-launcher"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open Live Support Chat"
      >
        <span className="support-widget-launcher__icon">
          {isOpen ? <CloseIcon size={18} /> : <SupportIcon size={18} />}
        </span>
        {!isOpen && unreadCount > 0 && (
          <span className="support-widget-launcher__badge">{unreadCount}</span>
        )}
        {!isOpen && <span className="support-widget-launcher__label">Support</span>}
      </button>
    </aside>
  );
};

export default UserSupportChatWidget;
